import React, { useEffect, useState } from 'react';

import {
  Dialog, DialogActions, DialogContent, DialogTitle, Grid, useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router';
import {
  Cell, Label, Pie, PieChart,
} from 'recharts';

import Button from '../../../components/Button';
import ContentCard from '../../../components/ContentCard';
import SkeletonStack from '../../../components/SkeletonStack';
import { useGetExamAttemptQuery, useSubmitExamMutation } from '../../../services/exams';
import useError from '../../../utils/react/hooks/useError';
import useSnackBar from '../../../utils/react/hooks/useSnackbar';

import {
  ChartWrapper, Mark, Title,
} from './index.styles';

interface ResultsDialogProps {
  open: boolean,
  onClose: () => void,
  mcqMarks?: number,
  mcqTotal?: number,
  subject: string,
  studentId: string,
  examId: string,
  attemptNum: number
}

const ResultsDialog = ({
  open,
  onClose,
  mcqMarks = 0,
  mcqTotal = 0,
  subject,
  studentId,
  examId,
  attemptNum,
}: ResultsDialogProps) => {
  const { data: examAttempt, isFetching } = useGetExamAttemptQuery({
    subject, examId, studentId, attemptNum,
  });

  const [submitExam,
    {
      isLoading: isSubmitExamLoading,
      isError: isSubmitExamError,
      isSuccess: isSubmitExamSuccess,
      error: submitExamError,
    }] = useSubmitExamMutation();

  const [data, setData] = useState<undefined | {name: string, value: number}[]>();

  useEffect(() => {
    if (examAttempt) {
      setData([
        { name: 'Correct', value: examAttempt.data.mark },
        { name: 'Wrong', value: (examAttempt.data.maxMark || 0) - (examAttempt.data.mark || 0) },
      ]);
    }
  }, [examAttempt]);

  const theme = useTheme();
  const navigate = useNavigate();
  const createSnackbar = useSnackBar();

  const COLORS = [theme.palette.success.main, theme.palette.backdrop.main];

  const handleSubmit = async () => {
    await submitExam({
      subject,
      examId,
      studentId,
      attemptNum,
    });
    onClose();
  };

  /**
   * ERROR & SUCCESS HANDLING
   */

  useEffect(() => {
    if (isSubmitExamSuccess) {
      createSnackbar('Exam submitted', 'success');
      navigate('/teacher/marking');
    }
  }, [isSubmitExamSuccess]);

  useError(isSubmitExamError, submitExamError);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Results Summary</DialogTitle>
      <DialogContent>
        {
          (isFetching || !data || !examAttempt) ? (
            <SkeletonStack />
          ) :
            (
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={2}
              >
                <Grid item xs={6}>
                  <ChartWrapper>
                    <PieChart width={200} height={200}>
                      <Pie
                        data={data}
                        cx={100}
                        cy={100}
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        stroke="none"
                      >
                        <Label
                          value={`${examAttempt?.data.mark} / ${examAttempt?.data.maxMark}`}
                          position="center"
                          className="label"
                          style={{
                            fontWeight: 'bold',
                            fontFamily: 'poppins',
                          }}
                          fill={theme.palette.darkPrimary.main}
                        />
                        {data.map((entry, index) => (
                          <Cell key={entry.name} fill={COLORS[index]} style={{ outline: 'none' }} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartWrapper>
                </Grid>
                <Grid item container xs={6} spacing={3}>
                  {
                    mcqTotal > 0 && (
                      <Grid item xs={12}>
                        <ContentCard>
                          <Grid container>
                            <Grid item xs={12} md={8}>
                              <Title>Multiple Choice:</Title>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Mark>{mcqMarks} / {mcqTotal}</Mark>
                            </Grid>
                          </Grid>
                        </ContentCard>
                      </Grid>
                    )
                  }
                  {
                    examAttempt.data.maxMark - mcqTotal > 0 && (
                      <Grid item xs={12}>
                        <ContentCard>
                          <Grid container>
                            <Grid item xs={12} md={8}>
                              <Title>Written Response:</Title>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Mark>
                                {examAttempt.data.mark
                                - mcqMarks} / {examAttempt.data.maxMark - mcqTotal}
                              </Mark>
                            </Grid>
                          </Grid>
                        </ContentCard>
                      </Grid>
                    )
                  }
                </Grid>
              </Grid>
            )
        }
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="text"
          allCaps
          color="error"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          allCaps
          loading={isSubmitExamLoading}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResultsDialog;
